import { useState, useEffect, useCallback } from 'react';

const SUPPORTED_LANGS = ['de', 'en'];
const DEFAULT_LANG = 'de';

const LS_PREFIX = 'translations_';

let translationsCache = {};

function getStoredTranslations(lang) {
  try {
    const raw = localStorage.getItem(LS_PREFIX + lang);
    if (raw) {
      const data = JSON.parse(raw);
      if (data && typeof data === 'object' && Object.keys(data).length > 0) {
        return data;
      }
    }
  } catch (_) {}
  return null;
}

function storeTranslations(lang, data) {
  try {
    localStorage.setItem(LS_PREFIX + lang, JSON.stringify(data));
  } catch (_) {}
}

async function loadTranslationFile(lang) {
  if (translationsCache[lang]) return translationsCache[lang];
  try {
    const res = await fetch(`${process.env.PUBLIC_URL || ''}/locales/${lang}/translation.json`);
    if (res.ok) {
      const data = await res.json();
      translationsCache[lang] = data;
      storeTranslations(lang, data);
      return data;
    }
  } catch (e) {
    console.error(`Failed to load translations for ${lang}:`, e);
  }
  return null;
}

function resolveInitialLang() {
  const saved = localStorage.getItem('lang');
  return SUPPORTED_LANGS.includes(saved) ? saved : DEFAULT_LANG;
}

function resolveInitialTranslations(lang) {
  // In-memory cache first (instant on re-mount / HMR)
  if (translationsCache[lang]) return translationsCache[lang];
  // Fall back to localStorage (survives page reload / phone wake)
  return getStoredTranslations(lang) || {};
}

export function useTranslation() {
  const [lang, setLangState] = useState(resolveInitialLang);
  const [translations, setTranslations] = useState(() => resolveInitialTranslations(resolveInitialLang()));
  const [ready, setReady] = useState(() => Object.keys(resolveInitialTranslations(resolveInitialLang())).length > 0);

  useEffect(() => {
    let cancelled = false;
    // Immediately apply cached translations so we never show keys
    const cached = resolveInitialTranslations(lang);
    if (Object.keys(cached).length > 0) {
      setTranslations(cached);
      setReady(true);
    }
    // Then fetch the latest in the background
    loadTranslationFile(lang).then((data) => {
      if (!cancelled && data) {
        setTranslations(data);
        setReady(true);
      }
    });
    document.documentElement.lang = lang;
    return () => { cancelled = true; };
  }, [lang]);

  const setLang = useCallback((newLang) => {
    if (SUPPORTED_LANGS.includes(newLang)) {
      setLangState(newLang);
      localStorage.setItem('lang', newLang);
    }
  }, []);

  const t = useCallback((key, params) => {
    let text = translations[key] || key;
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        text = text.replace(new RegExp('\\{\\{' + k + '\\}\\}', 'g'), String(v));
        text = text.replace(new RegExp('\\{' + k + '\\}', 'g'), String(v));
      }
    }
    return text;
  }, [translations]);

  return { t, lang, setLang, ready };
}
